import { Card, CardContent } from "@dealer-center/core-components";
import type { AccountEntity } from "@dealer-center/ddc-api-account-interface";
import styled from "styled-components";
import type { MutableRefObject } from "react";
import { createRef, useEffect } from "react";
import { useState } from "react";
import LetterDetailArea from "./letter-detail-area";
import HeaderArea from "./header-area";
import { useLocalStorageHook } from "@dealer-center/core-components";
import { useLocalStorageKeys } from "@dealer-center/core-interfaces-components";
import { useOutletContext } from "react-router-dom";
import { useDealerCodesGetTypes } from "@dealer-center/ddc-pages-resources-fetch";
import type { resources } from "@dealer-center/ddc-pages-resources-interface";
import { LoadingIndicator } from "@dealer-center/core-custom-components";

const StyledCard = styled(Card)`
  .panel-body {
    flex-direction: column;
  }
`;
const StyledLetterArea = styled.div`
  overflow: scroll;
`;
export type resourceRefsInterface = Record<
  string,
  {
    ref: MutableRefObject<HTMLDivElement | null>;
  }
>;
const StyledLetterHeader = styled.h2`
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  color: black;
  &:before,
  &:after {
    background: #6d6d6d;
    height: 2px;
    flex: 1;
    content: "";
  }
  &:before {
    margin-right: 0.5em;
  }
  &:after {
    margin-left: 0.5em;
  }
  font-size: 2em;
  font-weight: 500;
`;
export function DashboardResources() {
  const { accountEntity } = useOutletContext<{
    accountEntity: AccountEntity;
  }>();
  const { id: dealerId } = accountEntity;
  const { data: initialResources, isFetching: initialResourcesFetching } =
    useDealerCodesGetTypes(accountEntity.id);
  const letterAreaRef = createRef<HTMLDivElement>();
  const [showOnlyCheckedToggle, setShowOnlyChecked] = useLocalStorageHook(
    useLocalStorageKeys.ddcPagesDashboardShowOnlyChecked,
    false
  );
  const [showOnlyConfigurableToggle, setShowOnlyConfigurable] =
    useLocalStorageHook(
      useLocalStorageKeys.ddcPagesDashboardShowOnlyConfigurable,
      false
    );
  const [resourceRefs, setResourceRefs] = useState<resourceRefsInterface>({});
  const [currentResources, setCurrentResources] = useState<
    resources[] | undefined
  >(initialResources);
  const [filteredResources, setFilteredResources] = useState<
    resources[] | undefined
  >([]);
  const [firstLetters, setFirstLetters] = useState<string[]>([]);

  useEffect(() => {
    if (!initialResources) return;
    setCurrentResources(initialResources);
  }, [initialResources]);
  useEffect(() => {
    if (!showOnlyCheckedToggle && !showOnlyConfigurableToggle) {
      setFilteredResources(currentResources);
      return;
    }
    if (!currentResources) return;
    const newFilteredResources = currentResources.filter(resource => {
      if (showOnlyCheckedToggle && !resource.granted) {
        return false;
      }
      if (showOnlyConfigurableToggle && !resource.configurable) {
        return false;
      }
      return true;
    });
    setFilteredResources(newFilteredResources);
  }, [currentResources, showOnlyCheckedToggle, showOnlyConfigurableToggle]);

  useEffect(() => {
    if (!filteredResources) return;
    if (filteredResources.length === 0) return;
    setFirstLetters(
      filteredResources
        .map(resource => {
          return resource.name[0].toUpperCase();
        })
        .filter((value, index, self) => {
          return self.indexOf(value) === index;
        })
    );
  }, [filteredResources]);

  useEffect(() => {
    const newResourceRefs: resourceRefsInterface = {};
    firstLetters.forEach(letter => {
      newResourceRefs[letter] = {
        ref: createRef<HTMLDivElement>()
      };
    });
    setResourceRefs(newResourceRefs);
  }, [firstLetters]);
  useEffect(() => {
    const handleResize = () => {
      if (!letterAreaRef.current) return;

      // need to calculate the padding under the ref rather than this magic number
      const magicPaddingNumber = 56;

      letterAreaRef.current.style.height =
        window.innerHeight -
        (window.pageYOffset +
          letterAreaRef.current.getBoundingClientRect().top) -
        magicPaddingNumber +
        "px";
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [letterAreaRef]);
  const handleShowOnlySelectedToggled = () => {
    setShowOnlyChecked(!showOnlyCheckedToggle);
  };
  const handleShowOnlyConfigurableToggled = () => {
    setShowOnlyConfigurable(!showOnlyConfigurableToggle);
  };
  const handleCheckBoxChanged = (resourceName: string, checked: boolean) => {
    if (!currentResources) return;

    // Create an immutable update to avoid reference issues
    setCurrentResources(prevResources =>
      prevResources?.map(resource =>
        resource.name === resourceName
          ? { ...resource, granted: checked }
          : resource
      )
    );
  };
  const handleResetClicked = () => {
    setShowOnlyChecked(false);
    setShowOnlyConfigurable(false);
    setCurrentResources(initialResources);
  };

  if (!filteredResources || !initialResources || !currentResources)
    return <LoadingIndicator actionName="Loading Resources" />;

  return (
    <StyledCard
      content={
        <CardContent>
          <HeaderArea
            currentResources={currentResources}
            dealerId={dealerId}
            existingResources={initialResources}
            firstLetters={firstLetters}
            handleShowOnlyConfigurableToggled={
              handleShowOnlyConfigurableToggled
            }
            handleShowOnlySelectedToggled={handleShowOnlySelectedToggled}
            resetClicked={handleResetClicked}
            resourceRefs={resourceRefs}
            showOnlyCheckedToggle={showOnlyCheckedToggle}
            showOnlyConfigurableToggle={showOnlyConfigurableToggle}
          />
          <StyledLetterArea ref={letterAreaRef}>
            {firstLetters.map(letter => {
              return (
                <div
                  id={`dashboard-resources-${letter}`}
                  key={letter}
                  ref={resourceRefs[letter]?.ref}
                >
                  <StyledLetterHeader>{letter}</StyledLetterHeader>
                  <LetterDetailArea
                    disabled={initialResourcesFetching}
                    letter={letter}
                    resources={filteredResources
                      .filter(resource => {
                        return resource.name[0].toUpperCase() === letter;
                      })
                      .sort((a, b) => {
                        return a.name.localeCompare(b.name);
                      })}
                    onChange={handleCheckBoxChanged}
                  />
                </div>
              );
            })}
          </StyledLetterArea>
        </CardContent>
      }
    />
  );
}

export default DashboardResources;
